import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { notificationsAPI, staffChatAPI, chatAPI } from '../services/api';
import { useSettings } from './SettingsContext';
import { getAccessToken, getUserBlob } from '../utils/authStorage';
import { isStaffUser } from '../utils/roles';

const NotificationContext = createContext(null);

const LS_MUTE_UNTIL = 'notif_mute_until';
const LS_PREFS = 'notif_prefs';

export function NotificationProvider({ children }) {
  const { systemSettings } = useSettings();
  const [notifications, setNotifications] = useState([]);
  const [banner, setBanner] = useState(null);
  const [prefs, setPrefs] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_PREFS);
      return raw ? JSON.parse(raw) : { toastEnabled: true, bannerEnabled: true, aggregateWindowMs: 8000 };
    } catch (_) {
      return { toastEnabled: true, bannerEnabled: true, aggregateWindowMs: 8000 };
    }
  });
  const muteUntilRef = useRef(() => {
    const raw = localStorage.getItem(LS_MUTE_UNTIL);
    const ts = raw ? parseInt(raw, 10) : 0;
    return isNaN(ts) ? 0 : ts;
  });
  const prevRoomsRef = useRef({});
  const wsConnsRef = useRef({});
  const lastMsgSeenRef = useRef({});
  const aggBufferRef = useRef([]);
  const aggTimerRef = useRef(null);
  const autoHideTimersRef = useRef({});

  const wsCooldownUntilRef = useRef(0);
  const seenBackendNotifIdsRef = useRef(new Set());
  const webPushSyncInFlightRef = useRef(false);
  const webPushLastSyncAtRef = useRef(0);
  const audioCtxRef = useRef(null);
  const lastSoundAtRef = useRef(0);
  // ردیابی آخرین پیام دیدهشده در هر اتاق جهت شناسایی پیام جدید در خارج از بخش چت
  const seenRoomLastMsgRef = useRef({});
  const chatPollInFlightRef = useRef(false);

  const WS_BASE = useMemo(() => {
    // خالی → تک-origin: از window.location بساز (ws/wss مطابق پروتکل)
    const rawEnv = (process.env.REACT_APP_API_URL ?? '').trim();
    const apiBase = rawEnv || (typeof window !== 'undefined' ? '' : 'http://localhost:8000');
    const apiDerived = (() => {
      if (!apiBase) {
        // تک-origin: ws(s)://host/ws از location
        if (typeof window !== 'undefined') {
          const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
          return `${proto}//${window.location.host}/ws`;
        }
        return null;
      }
      try {
        const u = new URL(apiBase, window.location.href);
        const wsProto = u.protocol === 'https:' ? 'wss:' : 'ws:';
        return `${wsProto}//${u.host}/ws`;
      } catch (_) {}
      return null;
    })();

    const explicit = process.env.REACT_APP_WS_URL;
    if (explicit) {
      const cleaned = explicit.replace(/\/$/, '');
      if (typeof window !== 'undefined' && apiDerived) {
        try {
          const exp = new URL(cleaned);
          const api = new URL(apiDerived);
          const localHosts = new Set(['localhost', '127.0.0.1']);
          if (localHosts.has(exp.hostname) && localHosts.has(api.hostname) && exp.port !== api.port) {
            return apiDerived;
          }
        } catch (_) {}
      }
      return cleaned;
    }

    if (apiDerived) return apiDerived;
    if (typeof window !== 'undefined') {
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${proto}//${window.location.host}/ws`;
    }
    return 'ws://localhost:8000/ws';
  }, []);

  const toastEnabled = !!prefs.toastEnabled;
  const bannerEnabled = !!prefs.bannerEnabled;
  const aggregateWindowMs = prefs.aggregateWindowMs || 8000;
  const bannerId = banner?.id;

  const isTokenExpired = useCallback((token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1] || ''));
      const expSec = payload?.exp;
      if (!expSec) return false;
      const nowSec = Math.floor(Date.now() / 1000);
      return expSec <= nowSec;
    } catch {
      return true;
    }
  }, []);

  const isMuted = useCallback(() => {
    const now = Date.now();
    const ts = muteUntilRef.current instanceof Function ? muteUntilRef.current() : muteUntilRef.current;
    return ts > now;
  }, []);

  const playNotificationSound = useCallback(() => {
    if (typeof window === 'undefined') return;
    const now = Date.now();
    if (now - (lastSoundAtRef.current || 0) < 1500) return;
    lastSoundAtRef.current = now;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    try {
      if (!audioCtxRef.current) audioCtxRef.current = new AudioCtx();
      const ctx = audioCtxRef.current;
      if (!ctx) return;
      if (ctx.state === 'suspended') {
        try { ctx.resume(); } catch (_) {}
      }

      const t0 = ctx.currentTime;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(0.14, t0 + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.28);
      gain.connect(ctx.destination);

      const o1 = ctx.createOscillator();
      o1.type = 'sine';
      o1.frequency.setValueAtTime(880, t0);
      o1.connect(gain);
      o1.start(t0);
      o1.stop(t0 + 0.18);

      const o2 = ctx.createOscillator();
      o2.type = 'sine';
      o2.frequency.setValueAtTime(1320, t0 + 0.09);
      o2.connect(gain);
      o2.start(t0 + 0.09);
      o2.stop(t0 + 0.28);

      setTimeout(() => {
        try { gain.disconnect(); } catch (_) {}
      }, 500);
    } catch (_) {}
  }, []);

  // P3 review fix: window.alert/confirm/prompt دیگر سراسری monkey-patch
  // نمی‌شوند (رفتار جانبی برای همه‌ی کد اپ) — صدای اعلان از مسیر
  // pushNotification پخش می‌شود.

  const pushNotification = useCallback((item) => {
    if (!toastEnabled || isMuted()) return;
    // aggregate: push to buffer and flush on timer
    aggBufferRef.current.push(item);
    if (!aggTimerRef.current) {
      aggTimerRef.current = setTimeout(() => {
        const buf = aggBufferRef.current.slice();
        aggBufferRef.current = [];
        aggTimerRef.current = null;
        // aggregate by type
        const grouped = buf.reduce((acc, it) => {
          acc[it.type] = acc[it.type] || [];
          acc[it.type].push(it);
          return acc;
        }, {});
        const final = Object.entries(grouped).map(([type, list]) => {
          if (list.length === 1) return list[0];
          const countRooms = new Set(list.map(i => i.roomId).filter(Boolean)).size;
          return {
            id: `agg-${type}-${Date.now()}`,
            type,
            title: type === 'message' ? `#${list.length} پیام جدید` :
                   type === 'assignment' ? `تغییر انتساب (${list.length})` :
                   'اعلان‌ها',
            description: type === 'message' ? `در ${countRooms} اتاق` : `${list.length} رویداد` ,
            createdAt: Date.now(),
            count: list.length,
          };
        });
        if (final.length) playNotificationSound();
        setNotifications(prev => [...final, ...prev].slice(0, 10));
        for (const n of final) {
          const id = n?.id;
          if (!id) continue;
          if (autoHideTimersRef.current[id]) continue;
          autoHideTimersRef.current[id] = setTimeout(() => {
            try { clearTimeout(autoHideTimersRef.current[id]); } catch (_) {}
            delete autoHideTimersRef.current[id];
            setNotifications(prev => prev.filter(x => x.id !== id));
          }, 30000);
        }
      }, aggregateWindowMs);
    }
  }, [aggregateWindowMs, isMuted, playNotificationSound, toastEnabled]);

  const removeNotification = useCallback((id) => {
    const t = autoHideTimersRef.current[id];
    if (t) {
      try { clearTimeout(t); } catch (_) {}
      delete autoHideTimersRef.current[id];
    }
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const showBanner = useCallback((b) => {
    if (!bannerEnabled) return;
    setBanner(b);
  }, [bannerEnabled]);

  const hideBanner = useCallback(() => setBanner(null), []);

  const muteForMinutes = useCallback((mins = 15) => {
    const until = Date.now() + mins * 60 * 1000;
    localStorage.setItem(LS_MUTE_UNTIL, String(until));
    muteUntilRef.current = until;
  }, []);

  const savePrefs = useCallback((next) => {
    setPrefs(next);
    try { localStorage.setItem(LS_PREFS, JSON.stringify(next)); } catch (_) {}
  }, []);

  const urlBase64ToUint8Array = useCallback((base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; i++) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }, []);

  const syncWebPush = useCallback(async ({ force = false } = {}) => {
    if (typeof window === 'undefined') return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return;

    const now = Date.now();
    if (!force && now - (webPushLastSyncAtRef.current || 0) < 60000) return;
    if (webPushSyncInFlightRef.current) return;
    webPushSyncInFlightRef.current = true;
    webPushLastSyncAtRef.current = now;

    try {
      const token = getAccessToken();
      if (!token || isTokenExpired(token)) return;

      let settings = {};
      try {
        const resp = await notificationsAPI.getSettings();
        settings = resp?.data || {};
      } catch (_) {}

      const pushEnabled = settings?.push_notifications !== false;

      const reg = await navigator.serviceWorker.ready.catch(() => null);
      if (!reg || !reg.pushManager) return;

      let sub = await reg.pushManager.getSubscription().catch(() => null);

      if (!pushEnabled) {
        if (sub) {
          const endpoint = sub.endpoint;
          try { await sub.unsubscribe(); } catch (_) {}
          try { await notificationsAPI.unsubscribePush(endpoint); } catch (_) {}
        }
        return;
      }

      if (Notification.permission !== 'granted') {
        const promptedKey = 'kadolar_push_prompted_v1';
        const alreadyPrompted = (() => {
          try { return localStorage.getItem(promptedKey) === '1'; } catch (_) { return false; }
        })();

        if (Notification.permission === 'default' && !alreadyPrompted) {
          try { localStorage.setItem(promptedKey, '1'); } catch (_) {}
          try { await Notification.requestPermission(); } catch (_) {}
        }
        if (Notification.permission !== 'granted') return;
      }

      if (!sub) {
        const pkResp = await notificationsAPI.getVapidPublicKey().catch(() => null);
        const publicKey = pkResp?.data?.public_key || '';
        if (!publicKey) return;

        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        }).catch(() => null);

        if (!sub) return;
      }

      try {
        const payload = (sub && typeof sub.toJSON === 'function') ? sub.toJSON() : sub;
        await notificationsAPI.subscribePush(payload);
      } catch (_) {}
    } finally {
      webPushSyncInFlightRef.current = false;
    }
  }, [isTokenExpired, urlBase64ToUint8Array]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (!('serviceWorker' in navigator)) return undefined;

    const onMessage = (event) => {
      const data = event?.data;
      if (!data || data.type !== 'PUSH_NOTIFICATION') return;
      const payload = data.payload || {};
      pushNotification({
        id: `push-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        type: payload.type || 'system',
        title: payload.title || 'اعلان جدید',
        description: payload.body || payload.message || '',
        createdAt: Date.now(),
      });
    };

    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [pushNotification]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    syncWebPush({ force: true });

    const onFocus = () => syncWebPush({ force: true });
    const onVis = () => {
      if (document.visibilityState === 'visible') syncWebPush({ force: true });
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [syncWebPush]);

  // Polling for rooms and metrics to generate notifications and banners
  useEffect(() => {
    let timerRooms;
    const wsConns = wsConnsRef.current;

    const shouldPoll = () => {
      if (typeof document !== 'undefined' && document.hidden) return false;
      const onStaffRoute =
        typeof window !== 'undefined' &&
        typeof window.location?.pathname === 'string' &&
        window.location.pathname.startsWith('/staff');
      if (!onStaffRoute) return false;
      const token = getAccessToken();
      if (!token || isTokenExpired(token)) return false;
      const raw = getUserBlob();
      try {
        const u = raw ? JSON.parse(raw) : null;
        // P3 (بند ۴.۶): نقش از گروه‌های `roles[]` خوانده می‌شود (نه فقط user_type)
        // — پیک/پیکی و پشتیبانی‌ای که user_type اش customer است باید پرسنل حساب شود.
        const isStaff = isStaffUser(u);
        return !!isStaff;
      } catch {
        return false;
      }
    };

    const pollRooms = async () => {
      try {
        const res = await staffChatAPI.getRooms();
        const rooms = Array.isArray(res.data?.results) ? res.data.results : (res.data || []);
        const prevMap = prevRoomsRef.current || {};
        const nextMap = {};
        let pendingCount = 0;
        let slaBreaches = 0;

        for (const r of rooms) {
          nextMap[r.id] = r;
          const prev = prevMap[r.id];
          // pending rooms (support, open, no seller)
          const isPending = (!r.seller && r.status !== 'closed');
          if (isPending) pendingCount++;
          // SLA first response breach
          if (
            r?.is_active &&
            r?.status !== 'closed' &&
            r.room_type === 'support' &&
            !r.first_response_at &&
            (parseInt(r.sla_first_response_seconds_remaining || 0, 10) === 0)
          ) {
            slaBreaches++;
          }
          // detect new message
          const lastMsgId = r.last_message?.id;
          const prevLastMsgId = prev?.last_message?.id;
          if (prev && lastMsgId && prevLastMsgId && lastMsgId !== prevLastMsgId) {
            pushNotification({
              id: `msg-${r.id}-${lastMsgId}`,
              type: 'message',
              title: `پیام جدید در اتاق #${r.id}`,
              description: r.last_message?.is_internal_note ? 'یادداشت داخلی' : (r.last_message?.content || ''),
              roomId: r.id,
              createdAt: Date.now(),
            });
          }
          // detect assignment change
          const sellerId = r.seller?.id || null;
          const prevSellerId = prev?.seller?.id || null;
          if (prev && sellerId !== prevSellerId) {
            pushNotification({
              id: `assign-${r.id}-${sellerId || 'none'}`,
              type: 'assignment',
              title: `انتساب اتاق #${r.id}`,
              description: sellerId ? `منتسب به کاربر ${sellerId}` : 'برداشت شد',
              roomId: r.id,
              createdAt: Date.now(),
            });
          }
        }

        prevRoomsRef.current = nextMap;

        // Ensure WebSocket connections for relevant rooms (assigned to me or pending in support)
        try {
          const enableWs =
            typeof window !== 'undefined' &&
            typeof window.location?.pathname === 'string' &&
            window.location.pathname.startsWith('/staff');

          const userStr = getUserBlob();
          const token = getAccessToken();
          let myId = null;
          if (userStr) {
            try { myId = JSON.parse(userStr)?.id || null; } catch { myId = null; }
          }
          if (!enableWs) {
            for (const rid of Object.keys(wsConns)) {
              try { wsConns[rid]?.close(); } catch {}
              delete wsConns[rid];
            }
          } else
          if (token && !isTokenExpired(token)) {
            const relevantRooms = rooms.filter(r => {
              const assignedToMe = !!myId && (r?.seller?.id === myId);
              return r?.is_active && assignedToMe && r?.status !== 'closed';
            });
            const maxWsRooms = 8;
            const limitedRooms = relevantRooms.slice(0, maxWsRooms);
            const desiredIds = new Set(limitedRooms.map(r => String(r.id)));

            // Close stale connections
            for (const rid of Object.keys(wsConns)) {
              if (!desiredIds.has(String(rid))) {
                try { wsConns[rid]?.close(); } catch {}
                delete wsConns[rid];
              }
            }

            const now = Date.now();
            if (wsCooldownUntilRef.current > now) return;

            // Open or refresh desired connections
            for (const r of limitedRooms) {
              const rid = String(r.id);
              const existing = wsConns[rid];
              const isOpen = existing && existing.readyState === WebSocket.OPEN;
              const isConnecting = existing && existing.readyState === WebSocket.CONNECTING;
              if (isOpen || isConnecting) continue;

              const url = `${WS_BASE}/staff/chat/${r.id}/?token=${encodeURIComponent(token)}`;
              try {
                const ws = new WebSocket(url);
                let didOpen = false;
                ws.onopen = () => {
                  didOpen = true;
                };
                ws.onmessage = (ev) => {
                  try {
                    const msg = JSON.parse(ev.data);
                    const t = msg?.type;
                    if (t === 'message.new') {
                      const roomId = msg.room_id || r.id;
                      const mid = msg.id;
                      if (mid) {
                        const lastSeen = lastMsgSeenRef.current[roomId];
                        if (lastSeen && lastSeen >= mid) return;
                        lastMsgSeenRef.current[roomId] = mid;
                      }
                      pushNotification({
                        id: `ws-msg-${roomId}-${mid || Date.now()}`,
                        type: 'message',
                        title: `پیام جدید در اتاق #${roomId}`,
                        description: msg.content || '',
                        roomId: roomId,
                        createdAt: Date.now(),
                      });
                    } else if (t === 'room.assign') {
                      const roomId = msg.room_id || r.id;
                      const assignedId = msg.assigned_to || msg.seller_id || null;
                      pushNotification({
                        id: `ws-assign-${roomId}-${assignedId || 'none'}-${Date.now()}`,
                        type: 'assignment',
                        title: `انتساب اتاق #${roomId}`,
                        description: assignedId ? `منتسب به کاربر ${assignedId}` : 'برداشت شد',
                        roomId: roomId,
                        createdAt: Date.now(),
                      });
                    } else if (t === 'room.close' || t === 'room.reopen') {
                      const roomId = msg.room_id || r.id;
                      pushNotification({
                        id: `ws-room-${t}-${roomId}-${Date.now()}`,
                        type: 'room',
                        title: t === 'room.close' ? `اتاق #${roomId} بسته شد` : `اتاق #${roomId} بازگشایی شد`,
                        description: '',
                        roomId: roomId,
                        createdAt: Date.now(),
                      });
                    } else if (t === 'size_request_new') {
                      const roomId = msg.room_id || r.id;
                      const rid = msg.id;
                      const auto = msg.is_auto ? ' (خودکار)' : '';
                      pushNotification({
                        id: `ws-size-${roomId}-${rid}-${Date.now()}`,
                        type: 'size_request',
                        title: `درخواست افزایش حجم جدید${auto} - اتاق #${roomId}`,
                        description: msg.reason || (msg.is_auto ? 'رسیدن به سقف حجم اتاق' : 'درخواست مشتری'),
                        roomId: roomId,
                        createdAt: Date.now(),
                      });
                    }
                  } catch (_) {}
                };
                ws.onerror = () => {};
                ws.onclose = () => {
                  setTimeout(() => {
                    if (desiredIds.has(String(rid))) {
                      try { delete wsConns[rid]; } catch {}
                    }
                  }, 2000);
                  if (!didOpen) {
                    wsCooldownUntilRef.current = Date.now() + 60000;
                    try { delete wsConns[rid]; } catch {}
                  }
                };
                wsConns[rid] = ws;
              } catch (_) {
                
              }
            }
          }
        } catch (_) {
          
        }

        // banners
        if (pendingCount > 0) {
          showBanner({
            id: 'pending-rooms',
            type: 'pending',
            title: 'اتاق‌های در انتظار',
            description: `${pendingCount} اتاق نیاز به انتساب دارد`,
          });
        } else if (bannerId === 'pending-rooms') {
          hideBanner();
        }

        if (slaBreaches > 0) {
          showBanner({
            id: 'sla-breach',
            type: 'sla',
            title: 'هشدار SLA پاسخ اول',
            description: `${slaBreaches} اتاق از SLA عبور کرده است`,
          });
        } else if (bannerId === 'sla-breach') {
          hideBanner();
        }
      } catch (_) {
        // ignore
      }
    };

    if (shouldPoll()) {
      pollRooms();
      timerRooms = setInterval(pollRooms, 10000);
    } else {
      hideBanner();
    }

    return () => {
      if (timerRooms) clearInterval(timerRooms);
      if (aggTimerRef.current) {
        clearTimeout(aggTimerRef.current);
        aggTimerRef.current = null;
      }
      // close all websockets
      try {
        for (const rid of Object.keys(wsConns)) {
          wsConns[rid]?.close();
          delete wsConns[rid];
        }
      } catch {}
    };
  }, [WS_BASE, bannerId, hideBanner, isTokenExpired, pushNotification, showBanner]);

  useEffect(() => {
    let timer;

    const shouldPoll = () => {
      if (typeof document !== 'undefined' && document.hidden) return false;
      const onStaffRoute =
        typeof window !== 'undefined' &&
        typeof window.location?.pathname === 'string' &&
        window.location.pathname.startsWith('/staff');
      if (onStaffRoute) return false;
      const token = getAccessToken();
      if (!token || isTokenExpired(token)) return false;
      return true;
    };

    if (!shouldPoll()) return undefined;

    const poll = async () => {
      try {
        // customer-scope: badge مشتری فقط اعلان‌های customer-type را می‌بیند (فاز ۴)
        const res = await notificationsAPI.list({ as: 'customer', is_read: false, page_size: 10 });
        const data = res?.data;
        const items = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
        if (!items.length) return;

        const newIds = [];
        for (const n of items) {
          const id = n?.id;
          if (!id) continue;
          if (seenBackendNotifIdsRef.current.has(id)) continue;
          seenBackendNotifIdsRef.current.add(id);
          newIds.push(id);

          pushNotification({
            id: `backend-${id}`,
            type: n?.notification_type || 'system',
            title: n?.title || 'اعلان جدید',
            description: n?.message || '',
            createdAt: Date.now(),
          });
        }

        // قبلاً اینجا خودکار markAsRead می‌شد و شمارنده هیچ‌وقت بالا نمی‌رفت — حذف شد تا کاربر با مشاهده اعلان (کلیک/ورود به تب) خوانده شود
        // if (newIds.length) { try { await notificationsAPI.markAsRead(newIds); } catch (_) {} }
      } catch (_) {}
    };

    poll();
    timer = setInterval(poll, 20000);
    return () => clearInterval(timer);
  }, [isTokenExpired, pushNotification]);

  // بهروزرسانی بلادرنگ پیامهای چت در خارج از بخش چت (برای همه کاربران، نه فقط کارکنان):
  // وقتی کاربر در سایر بخشهای سایت است و پیام جدیدی از طرف مقابل میرسد،
  // بدون نیاز به ورود به چت یا کلیک، یک اعلان (toast) نمایش میدهیم.
  // برای جلوگیری از ری-رندر بیهوده کل صفحه، فقط اعلان پیامهای جدید ارسال میشود.
  useEffect(() => {
    const shouldPoll = () => {
      if (typeof document !== 'undefined' && document.hidden) return false;
      const token = getAccessToken();
      if (!token || isTokenExpired(token)) return false;
      // فقط برای کاربران غیر کارکنان در خارج از مسیر /staff (کارکنان توسط منطق قبلی پوشش داده میشوند)
      const onStaffRoute =
        typeof window !== 'undefined' &&
        typeof window.location?.pathname === 'string' &&
        window.location.pathname.startsWith('/staff');
      if (onStaffRoute) return false;
      const raw = getUserBlob();
      try {
        const u = raw ? JSON.parse(raw) : null;
        // P3 (بند ۴.۶): همان ملاک مشترک — poller مشتری فقط برای غیرپرسنل
        // (با roles[]؛ نسخهٔ قبلی پیک/پیکی را مشتری می‌شناخت و برایش چت
        // مشتری پول می‌کرد).
        const isStaff = isStaffUser(u);
        return !isStaff;
      } catch {
        return true;
      }
    };

    if (!shouldPoll()) return undefined;

    const pollChats = async () => {
      if (chatPollInFlightRef.current) return;
      chatPollInFlightRef.current = true;
      try {
        const res = await chatAPI.getChats();
        const data = res?.data;
        const rooms = Array.isArray(data)
          ? data
          : (Array.isArray(data?.results) ? data.results : []);
        const prev = seenRoomLastMsgRef.current;
        const next = {};
        for (const r of rooms) {
          const lm = r?.last_message;
          const mid = lm?.id;
          const rid = r?.id;
          if (!rid) continue;
          next[rid] = mid ?? null;
          // فقط اگر پیام جدیدی رسیده و پیام متعلق به خود کاربر نیست، اعلان بده
          if (
            prev[rid] !== undefined &&
            mid != null &&
            prev[rid] !== mid &&
            lm?.sender?.id != null &&
            lm.sender.id !== (() => {
              try { return JSON.parse(getUserBlob() || '{}')?.id; } catch { return null; }
            })()
          ) {
            pushNotification({
              id: `chat-msg-${rid}-${mid}`,
              type: 'message',
              title: `پیام جدید در ${r.product_name || r.name || 'گفتگو'}`,
              description: (lm?.message_type && lm.message_type !== 'text')
                ? `[${lm.message_type}]`
                : (lm?.content || ''),
              roomId: rid,
              createdAt: Date.now(),
            });
          }
        }
        seenRoomLastMsgRef.current = next;
      } catch (_) {
        // در صورت خطا (مثلاً شبکه)، نوبت بعد دوباره تلاش میکنیم
      } finally {
        chatPollInFlightRef.current = false;
      }
    };

    pollChats();
    const t = setInterval(pollChats, 8000);
    return () => clearInterval(t);
  }, [isTokenExpired, pushNotification]);

  // Maintenance banner based on backend read-only flag
  useEffect(() => {
    if (!bannerEnabled) return;
    const ro = !!systemSettings.read_only_mode;
    if (ro) {
      showBanner({
        id: 'maintenance',
        type: 'maintenance',
        title: 'نگهداری سیستم',
        description: systemSettings.read_only_message || 'سیستم به‌صورت موقت فقط خواندنی است.',
      });
    } else if (bannerId === 'maintenance') {
      hideBanner();
    }
  }, [bannerEnabled, bannerId, hideBanner, showBanner, systemSettings.read_only_mode, systemSettings.read_only_message]);

  const value = useMemo(() => ({
    notifications,
    removeNotification,
    banner,
    hideBanner,
    muteForMinutes,
    prefs,
    savePrefs,
    pushNotification,
    playNotificationSound,
  }), [banner, hideBanner, muteForMinutes, notifications, prefs, pushNotification, removeNotification, savePrefs, playNotificationSound]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export const useNotifications = () => useContext(NotificationContext);
